import type { InfiniteData, QueryKey } from '@tanstack/react-query'

import type { ClientError } from '@tribeplatform/gql-client/lib'
import type {
  Action,
  MutationAddReactionArgs,
  PaginatedPost,
  Post,
  PostReactionParticipant,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getPostKey } from '../../utils/keys/post.key.js'
import { useAuthMember } from '../auth/useAuthMember.js'
import {
  getCachedPost,
  addReactionToPost,
  infinitePostArrayUpdater,
  infinitePostUpdater,
  getCachedRootPostByReplies,
} from '../cache/useCachedPost.js'
import { pinnedPostsFilter, postsQueryFilter } from './filters.js'

type Snapshot = [
  QueryKey,
  Post | InfiniteData<PaginatedPost> | InfiniteData<Post>,
][]

export const useAddReaction = (options?: {
  useMutationOptions?: UseMutationOptions<
    Action,
    ClientError,
    MutationAddReactionArgs,
    Snapshot
  >
}) => {
  const queryClient = useQueryClient()
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const { data: authMember } = useAuthMember()
  const userReaction: PostReactionParticipant = {
    participant: {
      ...authMember,
    },
  }

  return useMutation<Action, ClientError, MutationAddReactionArgs, Snapshot>(
    (input: MutationAddReactionArgs) => client.posts.addReaction(input),

    {
      onMutate: async addReactionArgs => {
        const { reaction, overrideSingleChoiceReactions } =
          addReactionArgs.input
        // updating the post itself
        const postKey = getPostKey({
          variables: { id: addReactionArgs?.postId },
        })
        await queryClient.cancelQueries(postKey)

        const postSnapshot = getCachedPost(addReactionArgs?.postId, queryClient)

        if (postSnapshot) {
          queryClient.setQueriesData<Post>(
            postKey,
            addReactionToPost(
              reaction,
              userReaction,
              overrideSingleChoiceReactions,
            ),
          )
        }

        // updating pinned posts
        const pinnedPostsSnapshot =
          queryClient.getQueriesData<InfiniteData<Post>>(pinnedPostsFilter)
        if (pinnedPostsSnapshot) {
          queryClient.setQueriesData<Post[]>(pinnedPostsFilter, oldPosts =>
            oldPosts.map(
              infinitePostArrayUpdater(
                addReactionArgs.postId,
                addReactionToPost(
                  reaction,
                  userReaction,
                  overrideSingleChoiceReactions,
                ),
                true,
              ),
            ),
          )
        }
        // take a snapshot from all matched posts queries
        const postsSnapshots =
          queryClient.getQueriesData<InfiniteData<PaginatedPost>>(
            postsQueryFilter,
          )

        // update posts and replies within the found post everywhere (posts and the feed queries)
        queryClient.setQueriesData<InfiniteData<PaginatedPost>>(
          postsQueryFilter,
          infinitePostUpdater(
            addReactionArgs.postId,
            addReactionToPost(
              reaction,
              userReaction,
              overrideSingleChoiceReactions,
            ),
            true,
          ),
        )

        // updating root post if adding the reaction on a reply if no feed/posts have found
        let rootPostSnapshot
        let rootPostKey
        if (!pinnedPostsSnapshot?.length && !postsSnapshots?.length) {
          const rootPost = getCachedRootPostByReplies(
            addReactionArgs.postId,
            queryClient,
          )
          if (
            rootPost &&
            rootPost.replies &&
            rootPost.id !== addReactionArgs.postId
          ) {
            rootPostKey = getPostKey({ variables: { id: rootPost?.id } })
            rootPostSnapshot = queryClient.getQueryData<Post>(rootPostKey)
            if (rootPostSnapshot) {
              queryClient.setQueriesData<Post>(
                rootPostKey,
                infinitePostArrayUpdater(
                  addReactionArgs.postId,
                  addReactionToPost(
                    reaction,
                    userReaction,
                    overrideSingleChoiceReactions,
                  ),
                  true,
                ),
              )
            }
          }
        }

        return [
          [postKey, postSnapshot],
          [rootPostKey, rootPostSnapshot],
          ...pinnedPostsSnapshot,
          ...postsSnapshots,
        ]
      },
      onError: (error, variables, context) => {
        context.forEach(([queryKey, snapshot]) => {
          if (queryKey) {
            queryClient.setQueriesData(queryKey, snapshot)
          }
        })
      },
      ...useMutationOptions,
    },
  )
}
